import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CreateOrderHelper } from '@complete-order/classes/create.order.helper.class';
import { CompleteOrderBackendService } from '@complete-order/services/complete-order-backend.service';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { CreateOrderResponse } from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as fromCreateOrder from '../actions/create.order.action';
import * as fromDirectDebitAutofill from '../actions/direct.debit.autofill.action';

@Injectable()
export class CreateOrderEffects {
  orderCreated = false;
  constructor(
    public readonly actions$: Actions,
    public readonly completeOrderBackend: CompleteOrderBackendService,
    public readonly completeOrderService: CompleteOrderService,
    public readonly viewOffersService: ViewOffersService,
    private readonly router: Router,
    public readonly buildConfigService: BuildConfigService
  ) {}

  resetDirectDebitConfig$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDirectDebitAutofill.resetDirectDebitConfig),
        withLatestFrom(this.viewOffersService.currentPlan$),
        tap(([, plan]) => {
          this.completeOrderService.updateDirectDebitConfig({
            reuse_dd_details:
              this.completeOrderService.isDirectDebitAutofillAvailable(plan),
            payment_token: plan?.dd_details?.payment_token,
          });

          this.viewOffersService;
        })
      ),
    { dispatch: false }
  );

  cacheOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCreateOrder.cacheOrder),
      switchMap(() => this.completeOrderService.getCreateOrderRequestArgs$),
      withLatestFrom(
        this.completeOrderService.directDebitReuse$,
        this.completeOrderService.directDebitPaymentToken$,
        this.completeOrderService.directDebitValidationError$,
        this.completeOrderService.createOrderLoading$
      ),
      filter(() => !this.orderCreated),
      debounceTime(100),
      switchMap(([payload, reuseDD, ddPaymentToken, ddErrors]) => {
        const {
          offerReference,
          currentPlan,
          cost,
          extras,
          itemOffer,
          currentItem,
          protection,
          selected,
          form,
          orderNumber,
        } = payload;

        const request = new CreateOrderHelper(
          currentPlan,
          currentItem,
          cost,
          selected,
          protection,
          itemOffer,
          extras,
          form,
          orderNumber,
          offerReference,
          reuseDD,
          ddPaymentToken,
          ddErrors
        ).requestBody;

        return this.completeOrderBackend.cacheOrderForOffer(request).pipe(
          map(() => {
            return fromCreateOrder.cacheOrderSuccess();
          })
        );
      })
    )
  );

  submitOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCreateOrder.submitOrder),
      switchMap(() => this.completeOrderService.getCreateOrderRequestArgs$),
      withLatestFrom(
        this.completeOrderService.directDebitReuse$,
        this.completeOrderService.directDebitPaymentToken$,
        this.completeOrderService.directDebitValidationError$,
        this.completeOrderService.createOrderLoading$
      ),
      filter(() => !this.orderCreated),
      debounceTime(100),
      switchMap(([payload, reuseDD, ddPaymentToken, ddErrors]) => {
        const {
          offerReference,
          currentPlan,
          cost,
          extras,
          itemOffer,
          currentItem,
          protection,
          selected,
          form,
          orderNumber,
        } = payload;

        const request = new CreateOrderHelper(
          currentPlan,
          currentItem,
          cost,
          selected,
          protection,
          itemOffer,
          extras,
          form,
          orderNumber,
          offerReference,
          reuseDD,
          ddPaymentToken,
          ddErrors
        ).requestBody;

        return this.completeOrderBackend.submitOrderForOffer(request).pipe(
          map(() => {
            return fromCreateOrder.submitOrderSuccess();
          })
        );
      })
    )
  );

  loadCreateOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCreateOrder.loadCreateOrder),
      switchMap(() => this.completeOrderService.getCreateOrderRequestArgs$),
      withLatestFrom(
        this.completeOrderService.directDebitReuse$,
        this.completeOrderService.directDebitPaymentToken$,
        this.completeOrderService.directDebitValidationError$,
        this.completeOrderService.createOrderLoading$
      ),
      filter(() => !this.orderCreated),
      debounceTime(500),
      switchMap(([payload, reuseDD, ddPaymentToken, ddErrors]) => {
        const {
          offerReference,
          currentPlan,
          cost,
          extras,
          itemOffer,
          currentItem,
          protection,
          selected,
          form,
          orderNumber,
        } = payload;

        const request = new CreateOrderHelper(
          currentPlan,
          currentItem,
          cost,
          selected,
          protection,
          itemOffer,
          extras,
          form,
          orderNumber,
          offerReference,
          reuseDD,
          ddPaymentToken,
          ddErrors
        ).requestBody;

        return this.completeOrderBackend
          .postCreateOrder(
            `${this.buildConfigService.config.pr}/v2/offers/order`,
            request
          )
          .pipe(
            map((response) => {
              this.orderCreated = true;
              // Used to flag when a payment is processing
              sessionStorage.setItem(
                'prPaymentProcessing',
                JSON.stringify({ processing: false })
              );
              this.router.navigate(['/order-confirmation'], {
                replaceUrl: true,
                queryParams: { PRRef: offerReference },
              });
              return fromCreateOrder.loadCreateOrderSuccess({
                payload: response as CreateOrderResponse,
              });
            }),

            catchError((error) => {
              // Used to flag when a payment is processing
              sessionStorage.setItem(
                'prPaymentProcessing',
                JSON.stringify({ processing: false })
              );

              if (
                error.error?.metaData?.payment_amount &&
                error.error?.metaData?.payment_ref
              ) {
                this.router.navigate(['/offer-error'], {
                  replaceUrl: true,
                });
              }

              return of(
                fromCreateOrder.loadCreateOrderFail({
                  payload: 'error',
                })
              );
            })
          );
      })
    )
  );
}
